import { ref, computed } from 'vue'
import type {
  SignalDTO,
  SignalRootEntity,
  MyClientSignalDTO
} from '@/api/generated-signals'
import { getGlobalT } from '@/i18n'
import { initApiService } from '@/services/init'
import { fetchAllPages } from '@/utils/fetchAllPages'
import { isSilenceableDashboardError, extractApiErrorMessage, logUnexpectedFailure } from '@/utils/errorHandling'

/**
 * Poll interval for the consolidated dashboard signals fetch. Mirrors the
 * backend's 15-minute safety-net sync at a UI cadence so resolved signals
 * disappear (and new ones appear) without a manual refresh.
 */
const DASHBOARD_POLL_INTERVAL_MS = 15_000

/**
 * Root entity types that represent shared environmental configuration
 * (org/location/practitioner/insurer master data) rather than per-client work.
 * Signals on these entities can only be resolved by an organization admin —
 * they are not actionable for behandelaars.
 */
const ENV_LEVEL_ROOT_ENTITIES: SignalRootEntity[] = [
  'ORGANIZATION',
  'LOCATION',
  'PRACTITIONER',
  'INSURER'
]

function isEnvLevelSignal(signal: SignalDTO): boolean {
  return ENV_LEVEL_ROOT_ENTITIES.includes(signal.rootEntityType)
}

/**
 * The whole-organisation sweep per root entity type that is currently running,
 * shared by every `useSignals()` instance. A sweep is up to 50 pages and its
 * result does not depend on who asked, so a caller arriving while one is in
 * flight joins it instead of starting a second, interleaved one (#4161).
 */
const inFlightSweeps = new Map<SignalRootEntity, Promise<SignalDTO[]>>()

function sweepAllSignals(rootEntityType: SignalRootEntity): Promise<SignalDTO[]> {
  const pending = inFlightSweeps.get(rootEntityType)
  if (pending) return pending

  const api = initApiService().signals
  const sweep = fetchAllPages<SignalDTO>((page, size) =>
    api.listSignals(rootEntityType, undefined, page, size)
  ).finally(() => inFlightSweeps.delete(rootEntityType))
  inFlightSweeps.set(rootEntityType, sweep)
  return sweep
}

/**
 * Composable for managing signals (signaleringen).
 *
 * Provides reactive signal data with loading states and error handling.
 * Signals can be queried by audience (`/visible-to-me`), by client
 * (`/clients/{id}/signals`), or by root entity for entity-specific surfaces
 * (e.g. the appointment detail panel).
 */
export function useSignals() {
  const t = getGlobalT()

  const signals = ref<SignalDTO[]>([])
  const visibleToMeCount = ref(0)
  const blockingNonActionableSignals = ref<SignalDTO[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Consolidated dashboard buckets, populated by a single `loadDashboardSignals()`
  // call instead of one request per card. Each bucket keeps its own server-side
  // pagination (see `getDashboardSignals` defaults); `organizationLevel` is empty
  // for non-admins because the backend omits that bucket from the response.
  const myClientSignals = ref<MyClientSignalDTO[]>([])
  const personalPractitionerSignals = ref<SignalDTO[]>([])
  const billingAsPractitionerSignals = ref<MyClientSignalDTO[]>([])
  const organizationLevelSignals = ref<SignalDTO[]>([])
  // Server-side totals per bucket (#3310): the cards show one page of items,
  // but must surface the real backlog so the dashboard adds up to the bell.
  const myClientSignalsTotal = ref(0)
  const personalPractitionerSignalsTotal = ref(0)
  const billingAsPractitionerSignalsTotal = ref(0)
  const organizationLevelSignalsTotal = ref(0)
  // Shared loading/error for the dashboard fetch. `dashboardLoading` only ever
  // transitions true -> false (after the first load) so poll ticks don't reflash
  // the per-card skeletons.
  const dashboardLoading = ref(true)
  const dashboardError = ref<string | null>(null)
  let dashboardInFlight = false
  let dashboardPollHandle: ReturnType<typeof setInterval> | null = null

  /**
   * Signals grouped by rootEntityId for efficient lookup
   */
  const signalsByEntityId = computed(() => {
    const map = new Map<string, SignalDTO[]>()
    for (const signal of signals.value) {
      const existing = map.get(signal.rootEntityId) ?? []
      existing.push(signal)
      map.set(signal.rootEntityId, existing)
    }
    return map
  })

  /**
   * Run a paginated signal fetch with shared loading/error handling, paging
   * through all results via `fetchAllPages`. Backs the blocking-banner fetch.
   *
   * Permission-denied (401/403) and transient/cancelled network errors are
   * swallowed into an empty result without surfacing an error message or a
   * `console.error`: such outcomes are expected (the user lacks the permission,
   * or navigated away mid-load). Genuine failures (5xx with a response) still
   * set `error` and are logged.
   */
  async function safeFetchSignals<T>(
    context: string,
    fetchPage: (page: number, size: number) => Promise<{ data: { content?: T[]; last?: boolean } }>
  ): Promise<T[]> {
    loading.value = true
    error.value = null

    try {
      return await fetchAllPages<T>(fetchPage)
    } catch (err) {
      if (isSilenceableDashboardError(err)) return []
      error.value = extractApiErrorMessage(err, t('errors.loadSignals'))
      logUnexpectedFailure(`Error loading ${context} signals:`, err)
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch signals filtered by root entity type and (optionally) ID.
   */
  async function fetchSignals(
    rootEntityType: SignalRootEntity,
    rootEntityId?: string,
    page = 0,
    size = 20
  ) {
    loading.value = true
    error.value = null

    try {
      const api = initApiService().signals
      const response = await api.listSignals(rootEntityType, rootEntityId, page, size)
      signals.value = response.data.content ?? []
    } catch (err) {
      error.value = extractApiErrorMessage(err, t('errors.loadSignals'))
      logUnexpectedFailure('Error loading signals:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch all signals for a root entity type without pagination limits.
   * Automatically handles pagination to retrieve all results; joins a sweep of
   * the same type that is already in flight rather than starting another.
   */
  async function fetchAllSignals(rootEntityType: SignalRootEntity) {
    loading.value = true
    error.value = null

    try {
      signals.value = await sweepAllSignals(rootEntityType)
    } catch (err) {
      error.value = extractApiErrorMessage(err, t('errors.loadSignals'))
      logUnexpectedFailure('Error loading signals:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Get signals for a specific entity ID
   */
  function getSignalsForEntity(entityId: string): SignalDTO[] {
    return signalsByEntityId.value.get(entityId) ?? []
  }

  /**
   * Get signal count for a specific entity ID
   */
  function getSignalCountForEntity(entityId: string): number {
    return signalsByEntityId.value.get(entityId)?.length ?? 0
  }

  /**
   * Fetch the total count of signals visible to the current user.
   * Backs the SideMenu badge via /api/v1/signals/visible-to-me, which encapsulates
   * audience rules for behandelaar/practitioner/org admin without per-context fan-out.
   */
  async function fetchVisibleToMeCount() {
    loading.value = true
    error.value = null

    try {
      const api = initApiService().signals
      const response = await api.listSignalsVisibleToMe(0, 1)
      visibleToMeCount.value = response.data.totalElements ?? 0
    } catch (err) {
      error.value = extractApiErrorMessage(err, t('errors.loadSignalCount'))
      logUnexpectedFailure('Error loading visible-to-me signal count:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch env-level signals (organisation/location/practitioner/insurer) that
   * the current user can see but cannot resolve themselves. Used to show a
   * read-only "billing paused" banner to behandelaars when org admins still
   * need to fill in master data. Org admins receive these signals as part of
   * their normal actionable list and should not see the banner — gating is
   * the caller's responsibility (this composable returns the raw list).
   */
  async function fetchBlockingNonActionableSignals(): Promise<SignalDTO[]> {
    const api = initApiService().signals
    const all = await safeFetchSignals<SignalDTO>('blocking non-actionable', (page, size) =>
      api.listSignalsVisibleToMe(page, size)
    )
    const filtered = all.filter(isEnvLevelSignal)
    blockingNonActionableSignals.value = filtered
    return filtered
  }

  /**
   * Fetch signals whose entity chain resolves to the given client.
   * Uses the client-scope endpoint, which traverses the entity graph and finds
   * signals on appointments, care trajectories, documents, form submissions, and
   * appointment facts in addition to signals rooted directly on the client.
   */
  async function fetchSignalsForClient(clientId: string, page = 0, size = 20) {
    loading.value = true
    error.value = null

    try {
      const api = initApiService().signals
      const response = await api.listClientSignals(clientId, page, size)
      signals.value = response.data.content ?? []
    } catch (err) {
      error.value = extractApiErrorMessage(err, t('errors.loadSignals'))
      logUnexpectedFailure('Error loading signals:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch the total count of signals visible for a client, via the client-scope
   * endpoint. Mirrors `fetchSignalsForClient` but only requests the totalElements.
   */
  async function fetchCountForClient(clientId: string): Promise<number> {
    try {
      const api = initApiService().signals
      const response = await api.listClientSignals(clientId, 0, 1)
      return response.data.totalElements ?? 0
    } catch (err) {
      logUnexpectedFailure('Error loading signal count:', err)
      return 0
    }
  }

  /**
   * Load every dashboard signal bucket in a single request via
   * `GET /signals/dashboard`. Replaces the previous per-card fan-out (one
   * request per audience tier) that produced a synchronized burst on dashboard
   * load; the consolidated endpoint reuses each bucket's query semantics and
   * pagination server-side.
   *
   * Concurrent calls are coalesced via `dashboardInFlight` so a poll tick can't
   * overlap an in-progress load. Permission-denied / transient errors are
   * swallowed (buckets cleared, no error surfaced), mirroring the old per-card
   * behaviour; genuine failures set `dashboardError`.
   */
  async function loadDashboardSignals(): Promise<void> {
    if (dashboardInFlight) return
    dashboardInFlight = true
    dashboardError.value = null

    try {
      const api = initApiService().signals
      const { data } = await api.getDashboardSignals()
      myClientSignals.value = data.myClients.content ?? []
      personalPractitionerSignals.value = data.personalPractitioner.content ?? []
      billingAsPractitionerSignals.value = data.billingAsPractitioner.content ?? []
      organizationLevelSignals.value = data.organizationLevel?.content ?? []
      myClientSignalsTotal.value = data.myClients.totalElements ?? myClientSignals.value.length
      personalPractitionerSignalsTotal.value =
        data.personalPractitioner.totalElements ?? personalPractitionerSignals.value.length
      billingAsPractitionerSignalsTotal.value =
        data.billingAsPractitioner.totalElements ?? billingAsPractitionerSignals.value.length
      organizationLevelSignalsTotal.value =
        data.organizationLevel?.totalElements ?? organizationLevelSignals.value.length
    } catch (err) {
      if (isSilenceableDashboardError(err)) {
        myClientSignals.value = []
        personalPractitionerSignals.value = []
        billingAsPractitionerSignals.value = []
        organizationLevelSignals.value = []
        myClientSignalsTotal.value = 0
        personalPractitionerSignalsTotal.value = 0
        billingAsPractitionerSignalsTotal.value = 0
        organizationLevelSignalsTotal.value = 0
        return
      }
      dashboardError.value = extractApiErrorMessage(err, t('errors.loadSignals'))
      logUnexpectedFailure('Error loading dashboard signals:', err)
    } finally {
      dashboardLoading.value = false
      dashboardInFlight = false
    }
  }

  /**
   * Start polling the consolidated dashboard endpoint. Idempotent — clears any
   * existing timer first so callers don't stack intervals. Pair with
   * {@link stopDashboardSignalsPolling} in `onUnmounted`.
   */
  function startDashboardSignalsPolling() {
    stopDashboardSignalsPolling()
    dashboardPollHandle = setInterval(loadDashboardSignals, DASHBOARD_POLL_INTERVAL_MS)
  }

  function stopDashboardSignalsPolling() {
    if (dashboardPollHandle) {
      clearInterval(dashboardPollHandle)
      dashboardPollHandle = null
    }
  }

  return {
    signals,
    signalsByEntityId,
    visibleToMeCount,
    blockingNonActionableSignals,
    loading,
    error,
    fetchSignals,
    fetchAllSignals,
    fetchVisibleToMeCount,
    fetchBlockingNonActionableSignals,
    fetchSignalsForClient,
    fetchCountForClient,
    myClientSignals,
    personalPractitionerSignals,
    billingAsPractitionerSignals,
    organizationLevelSignals,
    myClientSignalsTotal,
    personalPractitionerSignalsTotal,
    billingAsPractitionerSignalsTotal,
    organizationLevelSignalsTotal,
    dashboardLoading,
    dashboardError,
    loadDashboardSignals,
    startDashboardSignalsPolling,
    stopDashboardSignalsPolling,
    getSignalsForEntity,
    getSignalCountForEntity
  }
}
